import { useEffect, useMemo, useState } from "react";
import { useCustom, useDataProvider } from "@refinedev/core";
import { useQuery } from "@tanstack/react-query";
import { useAssetById } from "hooks/useAssetById";
import { useStoryboardWithSlides } from "hooks/useStoryboardWithSlides/";
import { useBrandKit } from "hooks/useBrandKit/";
import { useStreamingTemplates } from "../components/slides/hooks/useStreamingTemplates";
import { mapServerTemplateToTemplateState } from "pages/media/components/storyboardEditor/helpers/mapToTemplateState";
import { extractTemplatesFromMediaSettings } from "pages/media/components/storyboardEditor/hooks/useTemplates";
import { TemplateType } from "pages/media/components/storyboardEditor/types";
import type { TemplateState } from "pages/media/components/storyboardEditor/types";
import { usePublicMediaAssets } from "hooks/usePublicMediaAssets/";
import { DEFAULT_MEDIA_VIDEO_ASPECT_RATIO } from "pages/media/constants/";
import { loadImageRatio } from "pages/media/components/storyboardEditor/helpers/";
import { calculateMediaAspectRatio } from "pages/media/components/storyboardEditor/helpers";
import type { MediaTemplateResponse } from "types/";
import type {
  MediaGenerationSettingsResponse,
  SlidesAsset,
  StoryboardAsset,
} from "pages/media/types/";
import type { MediaProjectResponse } from "pages/media/types";

type UseSlideCarouselDataParams = {
  project?: MediaProjectResponse;
  projectId?: string;
  storyboardId?: string;
};

export const useSlideCarouselData = ({
  project,
  projectId,
  storyboardId,
}: UseSlideCarouselDataParams) => {
  const storyboard = project?.storyboards?.find((s) => s.id === storyboardId);
  const organizationId = project?.organization_id;

  const { data: storyboardAsset } = useAssetById<StoryboardAsset>({
    assetId: storyboard?.asset_id,
  });

  const { data: slideAsset } = useAssetById<SlidesAsset>({
    assetId: storyboard?.slides_asset_id,
  });

  const storyboardPath = storyboardAsset?.data.path;
  const slidesPath = slideAsset?.data.path;

  const { sections, isLoading: isStoryboardLoading } = useStoryboardWithSlides(
    storyboardPath,
    slidesPath
  );

  const brandKitData = useBrandKit({
    brandKitId: project?.settings?.brand_kit_id,
    organizationId,
  });

  const { data: mediaSettingsData } =
    useCustom<MediaGenerationSettingsResponse>({
      url: `media/${organizationId}/projects/${projectId}/media/default/settings`,
      method: "get",
      queryOptions: {
        enabled: !!projectId && !!organizationId,
        queryKey: ["ProjectMediaSettings", projectId],
        // Match the storyboard editor's cache policy so we reuse
        // cached data instead of refetching (which may 404 during generation).
        staleTime: Infinity,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
      },
      errorNotification: false,
    });

  const slides = useMemo(
    () => sections.flatMap((section) => section.slides),
    [sections]
  );

  // Primary path: useStreamingTemplates for when slides exist (flash-like)
  const {
    templates: streamingTemplates,
    mediaAspectRatio: streamingAspectRatio,
    isLoading: isStreamingLoading,
  } = useStreamingTemplates({
    organizationId,
    mediaSettings: mediaSettingsData?.data,
    brandKitData,
    slides,
  });

  // ── Secondary path: fetch templates by ID (like the storyboard editor) ──
  // This gives us one TemplateState per unique template, keyed by template ID.
  const mediaSettings = mediaSettingsData?.data;

  const templateIdsAndTypes = useMemo(
    () => extractTemplatesFromMediaSettings(mediaSettings),
    [mediaSettings]
  );
  const templateIds = useMemo(
    () => Object.keys(templateIdsAndTypes),
    [templateIdsAndTypes]
  );

  const dataProvider = useDataProvider();
  const templateResource = `media/${organizationId}/templates`;

  const { data: templateData, isLoading: isTemplateFetchLoading } = useQuery({
    queryKey: ["carousel-templates", templateResource, templateIds],
    queryFn: async () => {
      const results = await Promise.all(
        templateIds.map((id) =>
          dataProvider().getOne<MediaTemplateResponse>({
            resource: templateResource,
            id,
          })
        )
      );
      return results.map((r) => r.data);
    },
    enabled: !!organizationId && templateIds.length > 0,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const { defaultPublicFont } = usePublicMediaAssets();

  const [logoRatio, setLogoRatio] = useState<number | undefined>();
  useEffect(() => {
    if (
      brandKitData.logo?.path &&
      !logoRatio &&
      !brandKitData.isBrandKitLoading
    ) {
      loadImageRatio(brandKitData.logo.path)
        .then(setLogoRatio)
        .catch(console.error);
    }
  }, [brandKitData.logo?.path, logoRatio, brandKitData.isBrandKitLoading]);

  const mediaAspectRatio = useMemo(() => {
    if (streamingAspectRatio) return streamingAspectRatio;
    if (mediaSettings?.width && mediaSettings.height) {
      return calculateMediaAspectRatio(
        mediaSettings.width / mediaSettings.height
      );
    }
    return DEFAULT_MEDIA_VIDEO_ASPECT_RATIO;
  }, [streamingAspectRatio, mediaSettings]);

  const commonAssets = useMemo(
    () => ({
      brandKitData,
      initialPresenter: undefined,
      defaultPublicFont,
      contact: undefined,
      presenterRatio: undefined,
      logoRatio,
    }),
    [brandKitData, defaultPublicFont, logoRatio]
  );

  const hasBrandKitPalette = !!brandKitData.brandKitPalette;

  // Templates mapped by ID (like the editor), one per unique template.
  const templatesByIdMap = useMemo(() => {
    if (!templateData || templateData.length === 0 || !hasBrandKitPalette)
      return new Map<string, TemplateState>();

    return new Map(
      templateData
        .filter((t) => t.id && templateIdsAndTypes[t.id])
        .map((t) => [
          t.id,
          mapServerTemplateToTemplateState(
            t.template,
            templateIdsAndTypes[t.id],
            mediaAspectRatio,
            commonAssets
          ),
        ])
    );
  }, [
    templateData,
    templateIdsAndTypes,
    mediaAspectRatio,
    commonAssets,
    hasBrandKitPalette,
  ]);

  // Build the ordered list of template IDs matching storyboard sections,
  // mirroring the storyboard editor's thumbnail order.
  const sectionTemplateIds = useMemo(() => {
    if (!mediaSettings) return [];
    const ids: string[] = [];
    if (mediaSettings.introduction_template_id) {
      ids.push(mediaSettings.introduction_template_id);
    }
    for (const chapter of mediaSettings.chapters) {
      if (chapter.chapter_template_id) {
        ids.push(chapter.chapter_template_id);
      }
      if (chapter.transition_template_id) {
        ids.push(chapter.transition_template_id);
      }
    }
    if (mediaSettings.closing_template_id) {
      ids.push(mediaSettings.closing_template_id);
    }
    return ids;
  }, [mediaSettings]);

  // Pick the best available templates for the carousel:
  // 1. If slides exist → use streamingTemplates (flash-like path)
  // 2. If media settings + fetched templates exist → use templatesByIdMap (editor-like path)
  // 3. Fallback → brand kit defaults
  const templates = useMemo(() => {
    if (slides.length > 0 && streamingTemplates.size > 0)
      return streamingTemplates;
    if (templatesByIdMap.size > 0) return templatesByIdMap;
    if (brandKitData.isBrandKitLoading || !hasBrandKitPalette)
      return new Map<string, TemplateState>();

    // Brand kit fallback
    const fallback = new Map<string, TemplateState>();
    for (const type of [
      TemplateType.INTRODUCTION,
      TemplateType.CHAPTER,
      TemplateType.TRANSITION,
      TemplateType.CLOSING,
    ]) {
      fallback.set(
        `default_${type}`,
        mapServerTemplateToTemplateState(
          undefined,
          type,
          mediaAspectRatio,
          commonAssets
        )
      );
    }
    return fallback;
  }, [
    slides.length,
    streamingTemplates,
    templatesByIdMap,
    brandKitData.isBrandKitLoading,
    hasBrandKitPalette,
    mediaAspectRatio,
    commonAssets,
  ]);

  const isLoading =
    isStoryboardLoading ||
    brandKitData.isBrandKitLoading ||
    (isStreamingLoading && isTemplateFetchLoading);

  return {
    sections,
    templates,
    sectionTemplateIds,
    brandKitData,
    mediaAspectRatio,
    isLoading,
  };
};
